import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import {
  DateFilterOptions,
  DateRangeSelectorComponent,
  DateRangeSelectorService,
  I18NDescriptor,
} from '@vendasta/uikit';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ResellerItem } from '../..';
import { ProductInformation } from './product-information';
import { Columns, ResellerItemMap } from './table.util';

@Component({
  selector: 'store-manage-product-table',
  templateUrl: './manage-product-table.component.html',
  styleUrls: ['./manage-product-table.component.scss'],
})
export class ManageProductTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() appIdToNameMap$: Observable<ResellerItemMap>;
  @Input() dataSource = new MatTableDataSource<ProductInformation>(Array(5));
  @Input() totalProductCnt$ = of(0);
  @Input() showSellingInStoreColumn = false;

  @ViewChild('manageProductSort', { static: false }) sort: MatSort;

  private applySearchFilter$$ = new BehaviorSubject<string>('');
  filterApplied = false;
  private productToolbarText$$ = new BehaviorSubject<string>('');

  displayedColumns: string[] = [
    Columns.Product,
    Columns.SellingInStore,
    Columns.ActiveAccounts,
    Columns.VerticalLine,
    Columns.Activations,
    Columns.Cancellations,
    Columns.Deactivations,
    Columns.Growth,
    Columns.Actions,
  ];
  dateFilterValue: I18NDescriptor = {
    key: '',
  };
  filters = [
    {
      icon: 'calendar_today',
      title: {
        key: 'FILTERS.DATE.CHIP',
      },
      text: this.dateFilterValue,
      enabled: true,
      dialog: {
        component: DateRangeSelectorComponent,
      },
    },
  ];
  config = [
    {
      canAddAndRemove: false,
    },
  ];

  Columns = Columns;

  private unsubscribe$ = new Subject<void>();

  constructor(private dateRangeService: DateRangeSelectorService, private translateService: TranslateService) {}

  ngOnInit(): void {
    if (!this.showSellingInStoreColumn) {
      const idx = this.displayedColumns.findIndex((c) => c === Columns.SellingInStore);
      this.displayedColumns.splice(idx, 1);
    }
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
    this.dataSource.filterPredicate = (data: ProductInformation, filter: string) => {
      const searchStringArr = [];
      searchStringArr.push(data.name);
      data.addons?.map((addon) => searchStringArr.push(addon.name));
      return searchStringArr.some((string) => {
        return string.toLowerCase().includes(filter);
      });
    };
    this.updateProductToolbarText();
    this.dateRangeService.setKey(DateFilterOptions.SevenDay);
    this.dateRangeService
      .getFilter()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((filter) => {
        if (!filter) {
          return;
        }
        this.dateFilterValue.key = filter.i18nKey;
      });
    this.applySearchFilter$$.pipe(debounceTime(250)).subscribe((filterValue) => this.applySearchFilter(filterValue));
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getProductId(product: ResellerItem): string | null {
    if (!product) {
      return null;
    }
    return product.addonId || product.appId;
  }

  get productToolbarText$(): Observable<string> {
    return combineLatest([this.productToolbarText$$.asObservable(), this.totalProductCnt$]).pipe(
      switchMap(([prodText, prodCnt]) => {
        const numShowing = this.filterApplied ? prodText : prodCnt;
        return this.translateService.get('FRONTEND.STORE.MANAGE_PRODUCT.MANAGE_PRODUCT_TABLE.TOOLBAR_TEXT', {
          numShowing: numShowing,
          numTotal: prodCnt,
        });
      }),
    );
  }

  handleSearchFilter(filterValue: string): void {
    this.applySearchFilter$$.next(filterValue);
  }

  private applySearchFilter(filterValue: string): void {
    this.filterApplied = !!filterValue;
    // To optimize rendering, hide the addons before the filter is removed,
    // and show the addons after the filter is applied.
    if (!this.filterApplied) {
      this.dataSource.data.forEach((d) => (d.expanded = false));
    }
    this.dataSource.filter = filterValue?.trim().toLowerCase();
    if (this.filterApplied) {
      this.dataSource.data.forEach((d) => (d.expanded = true));
    }
    this.updateProductToolbarText();
  }

  private updateProductToolbarText(): void {
    this.productToolbarText$$.next(`${this.dataSource.filteredData.length}`);
  }

  private sortingDataAccessor(data: ProductInformation, sortHeaderId: Columns): string | number {
    switch (sortHeaderId) {
      case Columns.Product:
        return data.name;
      case Columns.Activations:
        return data.change ? data.change.activations : 0;
      case Columns.Deactivations:
        return data.change ? data.change.deactivations : 0;
      case Columns.Growth:
        return data.growth;
      case Columns.ActiveAccounts:
        return data.stats ? (data.stats.activations || 0) - (data.stats.deactivations || 0) : 0;
      case Columns.Cancellations:
        return data.change ? data.change.cancellations : 0;
      default:
        return data[sortHeaderId];
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  trackByFn(_, item: ProductInformation): string {
    return item?.addonId || item?.appId;
  }
}
